
import React from 'react';
import { translations, Language } from '../translations';
import { User, FlightProfile } from '../types';
import { Map as MapIcon, Bot, Route, Folder, Menu } from 'lucide-react';

interface MobileNavBarProps {
    activeTab: string;
    onTabChange: (tab: any) => void;
    lang: Language;
    user: User | null;
    unreadMsgCount?: number;
    friendRequestCount?: number;
    profileIncomplete?: boolean;
}

export const MobileNavBar: React.FC<MobileNavBarProps> = ({ activeTab, onTabChange, lang, user, unreadMsgCount = 0, friendRequestCount = 0, profileIncomplete = false }) => {
    const t = translations[lang];

    const navItems = [
        { id: 'map', Icon: MapIcon, label: t.nav.terrain, disabled: false },
        { id: 'chat', Icon: Bot, label: t.nav.advisor, disabled: profileIncomplete },
        { id: 'planner', Icon: Route, label: lang === 'de' ? 'Planung' : 'Planning', disabled: profileIncomplete || (user?.permissions && !user.permissions.track_planning) },
        { id: 'library', Icon: Folder, label: t.nav.library, disabled: profileIncomplete },
        { id: 'menu', Icon: Menu, label: 'Menu' },
    ];

    return (
        <div className="lg:hidden fixed bottom-3 left-3 right-3 bg-slate-900/80 backdrop-blur-2xl border border-white/10 shadow-[0_8px_30px_rgb(0,0,0,0.5)] rounded-2xl pb-safe z-[5000]">
            <div className="flex justify-around items-center h-[4.5rem] px-1 relative">
                {navItems.map(item => {
                    const isActive = activeTab === item.id || (item.id === 'menu' && ['profile', 'admin', 'buddies', 'messages', 'market'].includes(activeTab));
                    const Icon = item.Icon;

                    return (
                        <button
                            key={item.id}
                            onClick={() => !item.disabled && onTabChange(item.id)}
                            className={`relative flex flex-col items-center justify-center w-full h-full group outline-none ${item.disabled ? 'opacity-30 grayscale cursor-not-allowed' : ''}`}
                        >
                            {/* Active Pill Indicator */}
                            <div className={`absolute inset-0 m-1.5 rounded-xl transition-all duration-300 ease-out ${isActive ? 'bg-sky-500/15 shadow-[inset_0_1px_1px_rgba(255,255,255,0.1)] scale-100 opacity-100' : 'bg-transparent scale-90 opacity-0'}`} />
                            
                            {/* Icon Container with Bounce/Slide */}
                            <div className={`relative z-10 transition-all duration-300 ease-out flex flex-col items-center ${isActive ? '-translate-y-1' : 'translate-y-1'}`}>
                                <Icon 
                                    className={`transition-all duration-300 ${isActive ? 'text-sky-400 drop-shadow-[0_0_8px_rgba(56,189,248,0.5)] w-6 h-6' : 'text-slate-400 w-5 h-5 group-hover:text-slate-300'}`} 
                                    strokeWidth={isActive ? 2.5 : 2} 
                                />
                                
                                {/* Label always visible */}
                                <span className={`absolute top-6 whitespace-nowrap text-[7.5px] sm:text-[9px] font-black uppercase tracking-normal sm:tracking-wider transition-all duration-300 ${isActive ? 'text-sky-400 translate-y-1' : 'text-slate-500'}`}>
                                    {item.label}
                                </span>
                            </div>

                            {/* Notification Badges */}
                            {item.id === 'menu' && (unreadMsgCount > 0 || friendRequestCount > 0) && (
                                <span className={`absolute top-3 right-[20%] w-2.5 h-2.5 bg-red-500 border-2 border-slate-900 rounded-full animate-pulse transition-transform ${isActive ? '-translate-y-1' : 'translate-y-1'}`} />
                            )}
                        </button>
                    );
                })}
            </div>
        </div>
    );
};
